import React from 'react';
import TeamPhoto from '../../../assets/images/Professional2.png';
import ScrollReveal from '../../../components/ScrollReveal';

const OurProcess = () => {
  const steps = [
    {
      number: '01',
      title: 'Discovery',
      description: "We listen, ask the right questions, and deeply understand your goals before writing a single line of code.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
    },
    {
      number: '02',
      title: 'Strategy',
      description: "We map out the architecture, define milestones, and assemble the perfect team for your project.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
    },
    {
      number: '03',
      title: 'Build',
      description: "Agile development with weekly demos. You see progress, provide feedback, and stay in control.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
    },
    {
      number: '04',
      title: 'Launch',
      description: "Rigorous testing, smooth deployment, and ongoing support to ensure your success.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
        </svg>
      ),
    },
  ];

  return (
    <section id="our-process" className="section-py bg-dark-900 text-white relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-dark-mesh opacity-50" />
      <div className="absolute inset-0 bg-grid opacity-20" />

      <div className="container-lg relative">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-10 md:mb-14">
          <ScrollReveal animation="fade-right">
            <div>
              <span className="label text-primary-400 mb-4 block">Our Process</span>
              <h2 className="heading-lg text-white mb-6">
                Simple.
                <br />
                Transparent.
                <br />
                <span className="text-gradient-blue">Effective.</span>
              </h2>
              <p className="text-lg text-dark-300 leading-relaxed">
                No black boxes. No surprises. Just clear communication, steady progress, and software that exceeds expectations.
              </p>
            </div>
          </ScrollReveal>

          {/* Image */}
          <ScrollReveal animation="fade-left" delay={200}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-500 to-accent-cyan rounded-3xl blur-2xl opacity-20" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={TeamPhoto}
                  alt="Our team collaborating"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/60 to-transparent" />
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <ScrollReveal key={step.number} delay={index * 100}>
              <div className="group relative h-full">
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(50%+3rem)] w-[calc(100%-3rem)] h-px bg-gradient-to-r from-primary-500/50 to-transparent" />
              )}

              <div className="relative bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-2xl p-8 hover:border-primary-500/50 hover:bg-dark-800 transition-all duration-500">
                {/* Step number */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-glow-sm group-hover:scale-110 transition-transform duration-500">
                    {step.number}
                  </div>
                  <div className="w-10 h-10 bg-dark-700 rounded-xl flex items-center justify-center text-primary-400 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                    {step.icon}
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-dark-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurProcess;
